import React, { useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  Paper, 
  Grid,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Link,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material'; // ✅ NUEVO: Iconos para mostrar/ocultar
import { useAuth } from '../../context/AuthContext';
import { getDefaultHomePath } from '../../types/auth';
import { BRAND, BRAND_COLORS, BRAND_GRADIENT } from '../../constants/branding';
import EapLogo from '../../components/brand/EapLogo';
import PrivacyNoticeLink from '../../components/common/PrivacyNoticeLink';
import { Link as RouterLink, useLocation } from 'react-router-dom';

const LoginPage: React.FC = () => {
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false); // ✅ NUEVO: Estado para mostrar/ocultar contraseña
  const navigate = useNavigate();
  const location = useLocation();
  const { login: authLogin, loading } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Obtener mensaje de éxito desde reset password
  const successMessage = location.state?.message;
  // Sesión expirada: redirección desde interceptor 401
  const sessionExpired = location.state?.sessionExpired;

  // ✅ NUEVO: Función para alternar visibilidad de contraseña
  const handleClickShowPassword = () => {
    setShowPassword(prev => !prev);
  };

  // ✅ NUEVO: Prevenir el mousedown en el botón del ojo
  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      setError('Por favor ingrese usuario y contraseña');
      return;
    }
    setError('');
    const response = await authLogin(username, password);
    if (response.success) {
      navigate(response.user ? getDefaultHomePath(response.user) : '/login');
    } else {
      setError(response.message || 'Error de autenticación');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: BRAND_GRADIENT,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Paper
        elevation={8}
        sx={{
          width: '100%',
          maxWidth: 1000,
          minHeight: 600,
          display: 'flex',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <Grid container sx={{ height: '100%' }}>
          {/* Panel Izquierdo - Imagen y Branding */}
          {!isMobile && (
            <Grid 
              item 
              md={6} 
              sx={{
                background: `linear-gradient(160deg, ${BRAND_COLORS.lightLavender} 0%, ${BRAND_COLORS.white} 70%)`,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                p: 4,
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundImage: `radial-gradient(${BRAND_COLORS.darkPurple}1f 1.2px, transparent 1.2px)`,
                  backgroundSize: '22px 22px',
                  opacity: 1,
                }
              }}
            >
              <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
                <EapLogo variant="full" />
                <Typography
                  variant="h6"
                  sx={{
                    color: 'text.secondary',
                    mt: 3,
                    lineHeight: 1.4,
                    fontSize: { xs: '1rem', md: '1.125rem' },
                    fontWeight: 500,
                  }}
                >
                  {BRAND.tagline}
                </Typography>
                <Box
                  sx={{
                    width: 60,
                    height: 3,
                    backgroundColor: 'primary.main',
                    mx: 'auto',
                    mt: 2,
                    borderRadius: 1.5,
                  }}
                />
              </Box>
            </Grid>
          )}
          
          {/* Panel Derecho - Formulario de Login */}
          <Grid 
            item 
            xs={12} 
            md={6} 
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              p: { xs: 3, md: 5 },
              backgroundColor: '#FFFFFF',
            }}
          >
            {/* Header del formulario */}
            <Box sx={{ mb: 4, textAlign: { xs: 'center', md: 'left' } }}>
              {/* Logo para móvil */}
              {isMobile && (
                <Box sx={{ mb: 2 }}>
                  <EapLogo variant="stacked" />
                </Box>
              )}
              
              <Typography 
                variant="h4" 
                component="h1" 
                sx={{ 
                  fontWeight: 700, 
                  color: 'primary.main', 
                  mb: 1,
                  fontSize: { xs: '1.75rem', md: '2rem' }
                }}
              >
                Iniciar Sesión
              </Typography>
              
              <Typography 
                variant="body1" 
                sx={{ 
                  color: 'text.secondary',
                  fontSize: '1rem'
                }}
              >
                Accede a tu cuenta para continuar
              </Typography>
            </Box>
            
            {/* Alert de éxito desde reset password */}
            {successMessage && (
              <Alert 
                severity="success" 
                sx={{ 
                  mb: 3,
                  borderRadius: 1,
                  '& .MuiAlert-message': {
                    fontSize: '0.875rem'
                  }
                }}
              >
                {successMessage}
              </Alert>
            )}

            {/* Sesión expirada: se mostró al redirigir por 401 TOKEN_EXPIRED */}
            {sessionExpired && (
              <Alert 
                severity="warning" 
                sx={{ 
                  mb: 3,
                  borderRadius: 1,
                  '& .MuiAlert-message': {
                    fontSize: '0.875rem'
                  }
                }}
              >
                Su sesión ha expirado. Por favor, inicie sesión de nuevo.
              </Alert>
            )}
            
            {/* Mensaje de error */}
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 3,
                  borderRadius: 1,
                  '& .MuiAlert-message': {
                    fontSize: '0.875rem'
                  }
                }}
              >
                {error}
              </Alert>
            )}
            
            {/* Formulario */}
            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="username"
                label="Usuario"
                name="username"
                autoComplete="username"
                autoFocus
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={loading}
                sx={{ mb: 2 }}
              />
              
              {/* ✅ MODIFICADO: Campo de contraseña con icono de ojo */}
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Contraseña"
                type={showPassword ? 'text' : 'password'} // ✅ Cambia entre text y password
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                sx={{ mb: 3 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="alternar visibilidad de contraseña"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                        disabled={loading}
                        sx={{
                          color: 'text.secondary',
                          '&:hover': {
                            color: 'primary.main',
                          },
                        }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{ 
                  mb: 2,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                }}
              >
                {loading ? (
                  <>
                    <CircularProgress size={24} sx={{ mr: 1, color: 'white' }} />
                    Iniciando sesión...
                  </>
                ) : (
                  'Iniciar Sesión'
                )}
              </Button>

              {/* Enlace "Olvidé mi contraseña" */}
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Link
                  component={RouterLink}
                  to="/forgot-password"
                  sx={{
                    textDecoration: 'none',
                    color: 'primary.main',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  ¿Olvidaste tu contraseña?
                </Link>
              </Box>
            </Box>
            
            {/* Footer */}
            <Box sx={{ textAlign: 'center', mt: 'auto', pt: 3 }}>
              <Typography variant="caption" color="text.secondary">
                {new Date().getFullYear()} © {BRAND.companyName}
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <PrivacyNoticeLink />
              </Box>
              <br />
              <Typography variant="caption" color="text.secondary">
               {/* Powered by ComPsych Integration */}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default LoginPage;